import React, {useMemo} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Box, Grid, Tooltip, Typography, useTheme} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useFormContext} from "react-hook-form";
import {IRADAncillary, IRaDetailView} from "../../models/models";
import {LANGUAGES, LOCALES} from "../../config";
import {FormCheckbox, FormNumberInput, FormTextField} from "../../common/form-fields-wrappers";
import {formatCurrency, getLocaleByLang} from "../../utils";
import {useRecoilValue} from "recoil";
import {useQueryClient} from "@tanstack/react-query";
import {raDetailState} from "../state";
import EmptyState from "../../../../util/component/EmptyState";
import {useHasScope} from "../../../../config/globalConfig";
import {NetworkScope} from "../../types";
import AncillaryPostSignActions from "../ancillary-post-sign/AncillaryPostSignActions";
import AncillaryHistorySection from "../ancillary-post-sign/AncillaryHistorySection";
import {IAncillaryDeskRules} from "../ancillary-post-sign/models";
import {formatDeskDiscount} from "../ancillary-post-sign/historyAmounts";
import useDeskDiscountRangeForRa from "../ancillary-post-sign/useDeskDiscountRangeForRa";
import {isPostSignAllowedForSegment} from "../ancillary-post-sign/postSignSegmentRules";
import {countAdditionalDrivers} from "../ancillary-post-sign/postSignAddRules";
import {IIndexedAncillary, partitionChangeGroup} from "../../create-ra/ra-filling/ancillaries-info/changeGroup";
import ChangeGroupMultiSelect, {IChangeGroupOption} from "../../create-ra/ra-filling/ancillaries-info/ChangeGroupMultiSelect";

// refreshRaDetail: refetch della query dettaglio RA (RaDetail.tsx:518). La query e' enabled:false, quindi
// queryClient.refetchQueries la SALTA (in react-query 4.13.0 refetchQueries filtra le query disabled con
// !query.isDisabled(), a prescindere da type:'all'); solo il refetch() dell'observer bypassa il filtro
// (come il caricamento iniziale a RaDetail.tsx:689). Serve per aggiornare "Informazioni Accessori" post add/remove.
const AncillariesInfo = ({refreshRaDetail}: {refreshRaDetail?: () => void}) => {
	const theme = useTheme();
	const {t, i18n} = useTranslation(['network']);
	const raDetail = useRecoilValue(raDetailState);
	const queryClient = useQueryClient();

	const canAddAncillary = useHasScope(NetworkScope.RaAncillaryAdd);
	const canRemoveAncillary = useHasScope(NetworkScope.RaAncillaryRemove);
	const canViewAncillaryHistory = useHasScope(NetworkScope.RaAncillaryHistory);

	// Range sconto banco dalla governance (location x profili), come al banco (feedback 5).
	const deskDiscountRange = useDeskDiscountRangeForRa();

	const raDetailForm = useFormContext<IRaDetailView>();

	const ancillariesFormValues = raDetailForm.getValues('ancillariesInfo');

	const idRentalAgreement: string = raDetail?.idRentalAgreement ?? '';
	const isOngoing: boolean = raDetail?.generalInfo?.raState?.code === 'ONGOING';
	// R3 (LPP-21950, VB-01): su abbonamenti (CarBox incluso) e credito pieno le operazioni accessori
	// post-firma non sono ammesse, quindi i pulsanti Aggiungi/Rimuovi non vengono mostrati affatto:
	// senza questo l'operatore vedrebbe azioni che il backend rifiuta. Stessa regola del gate NTW.
	const postSignAllowedForSegment: boolean = isPostSignAllowedForSegment(
		raDetail?.financialInfo?.segment, raDetail?.financialInfo?.creditType);
	// Token di concorrenza ottimistica (VT-13): NTW valida il valore lato server.
	const lastRequestUpdateDate: string = raDetail?.lastRequestUpdateDate ?? raDetail?.generalInfo?.lastRequestUpdateDate ?? '';
	// Ancillary rimovibili: quelle vendute al banco (origine desk), gia' presenti nel dettaglio.
	// R7 (LPP-21950): NTW espone `removable` per ciascun accessorio, calcolato con le stesse regole che
	// applica all'operazione di rimozione (origine, finestra time_to_remove, segmento). Si escludono solo
	// i `false`: `null`/assente significa valutazione non disponibile (RA non ONGOING, catalogo PRC non
	// recuperabile) e in quel caso si mantiene il comportamento precedente, invece di nascondere la
	// rimozione per un dato che non c'e'.
	const removableAncillaries: IRADAncillary[] = (ancillariesFormValues?.counterSale ?? [])
		.filter((a: IRADAncillary) => a?.removable !== false);
	// Catalogo vendibile post-firma calcolato e filtrato da NTW (flag_resellable_at_desk_post_ra +
	// finestra time_to_change + segmento, escluso il gia' venduto). Arriva con la shape NtwCounterSaleAncillary
	// (proxy SC-BE): va mappato sui campi attesi da IRADAncillary usati dalla modale post-firma.
	const mapPostSignAncillary = (a: any): IRADAncillary => ({
		...a,
		idPrcAncillary: a?.ancillaryId ?? a?.idPrcAncillary,
		ancillaryDefaultLabel: a?.ancillaryDefaultLabel,
		ancillarySubType: a?.subtype ?? a?.ancillarySubType,
		ancillaryServiceItemCode: a?.taxCode ?? a?.ancillaryServiceItemCode,
		ancillaryCurrency: a?.currency ?? a?.ancillaryCurrency,
		amountTaxIncluded: a?.priceVatIncluded ?? a?.amountTaxIncluded,
		maxUnits: a?.maxUnits,
		flagDiscountApplicable: a?.flagDiscountApplicable,
		timeToChangeMinutes: a?.timeToChange ?? null,
		timeToRemoveMinutes: a?.timeToRemove ?? null
	} as IRADAncillary);
	const addableAncillaries: IRADAncillary[] = (raDetail?.postSignSellableAncillaries ?? []).map(mapPostSignAncillary);

	// Regole di listino PRC per l'ancillary scelto; NTW rivalida comunque flag e finestre lato server.
	const resolveDeskRules = (ancillary: IRADAncillary): IAncillaryDeskRules => ({
		resellableAtDeskPostRa: (ancillary as any)?.flagResellableAtDeskPostRa ?? true,
		timeToChangeMinutes: (ancillary as any)?.timeToChangeMinutes ?? (ancillary as any)?.timeToChange ?? null,
		timeToRemoveMinutes: (ancillary as any)?.timeToRemoveMinutes ?? (ancillary as any)?.timeToRemove ?? null
	});

	const handleOperationCompleted = () => {
		if (idRentalAgreement) {
			// Storico: query attiva -> invalidate la rifà partire.
			queryClient.invalidateQueries({queryKey: ['ntw_ancillary_history', idRentalAgreement]});
			// Sezione "Informazioni Accessori": legge dal dettaglio RA (query enabled:false). NON usare
			// queryClient.refetchQueries (in 4.13.0 salta le query disabled via !query.isDisabled(), anche con
			// type:'all'): resterebbe stale. Il refetch() dell'observer bypassa il filtro e ripopola la sezione.
			refreshRaDetail?.();
		}
	};

	// NB: firma a 2 lingue, come sulla linea di certificazione. La versione di release/main estende il
	// tipo a Fr/Es/Pt, ma quei campi non esistono in IRADAncillary su questa linea (models.ts:267-269
	// ha solo ancillaryNameIt/En + ancillaryDefaultLabel): la firma estesa non compilerebbe.
	const ancillaryName = useMemo((): 'ancillaryNameIt' | 'ancillaryNameEn' => {
		let prop = 'ancillaryDefaultLabel';
		LANGUAGES.every((elem: string) => {
			if (i18n.language.includes(elem)) {
				prop = 'ancillaryName' + elem.charAt(0).toUpperCase() + elem.slice(1);
				return false;
			}
			return true;
		});

		return prop as 'ancillaryNameIt' | 'ancillaryNameEn';
	}, [i18n.language]);

	return (
		<>
			<Accordion>
				<AccordionSummary expandIcon={<ExpandMore />}>
					<Typography variant={"bodyLarge"}>
						{t('raDetail.ancillaries.accordion.title')}
					</Typography>
				</AccordionSummary>

				<AccordionDetails>
					{
						isOngoing &&
						<AncillaryPostSignActions
							idRentalAgreement={idRentalAgreement}
							isOngoing={isOngoing}
							lastRequestUpdateDate={lastRequestUpdateDate}
							addableAncillaries={addableAncillaries}
							removableAncillaries={removableAncillaries}
							existingAncillaries={[...(ancillariesFormValues?.booked ?? []), ...(ancillariesFormValues?.counterSale ?? [])]}
							resolveDeskRules={resolveDeskRules}
							canAdd={!!canAddAncillary && postSignAllowedForSegment}
							canRemove={!!canRemoveAncillary && postSignAllowedForSegment}
							deskDiscountRange={deskDiscountRange}
							onCompleted={handleOperationCompleted}
							existingAdditionalDrivers={countAdditionalDrivers(raDetail?.driversInfo)}
							vehicleEtollNumber={raDetail?.vehicle?.etollEquipmentNumber}
						/>
					}

					<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
						<Grid item xs={12}>
							<Typography variant={"bodyLarge"}>
								{t("raDetail.ancillaries.section.bookedAncillaries")}
							</Typography>
						</Grid>

						{
							// LPP-22293 (RDT §3 SCFE-2): stesso accorpamento di create-ra. Le ancillary
							// Change Group (^CG\d+$) diventano un singolo controllo multi-scelta (read-only
							// in dettaglio); le altre restano box separati invariati.
							(() => {
								const {changeGroup, others} = partitionChangeGroup<IRADAncillary>(ancillariesFormValues?.booked ?? []);
								return (
									<>
										{others.map(({ancillary, index}: IIndexedAncillary<IRADAncillary>) =>
											<AncillaryField key={`${ancillary.ancillaryCode}`} elem={ancillary} type={'booked'}
															index={index} ancillaryName={ancillaryName} disabled={true}/>
										)}
										{changeGroup.length > 0 &&
											<ChangeGroupField changeGroup={changeGroup} type={'booked'} ancillaryName={ancillaryName} />
										}
									</>
								);
							})()
						}

						{
							!ancillariesFormValues?.booked?.length &&
							<Grid item xs={12}>
								<EmptyState title={''} content={t('raDetail.ancillaries.message.noItems')}/>
							</Grid>
						}

						{
							!!ancillariesFormValues?.booked?.length &&
							<Grid item xs={12} sx={{pt: theme.spacing(2), display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"body"} component={"span"}>
									{t("raDetail.ancillaries.amount.booked")}
								</Typography>

								<Typography variant={"bodyLarge"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={raDetail?.quotationInfo.totAncillariesAmountBooked}
										currency={null}
									/>
								</Typography>
							</Grid>
						}
					</Grid>

					{/* Added Ancillaries */}
					<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
						<Grid item xs={12}>
							<Typography variant={"bodyLarge"}>
								{t("raDetail.ancillaries.section.addedAncillaries")}
							</Typography>
						</Grid>

						{
							ancillariesFormValues?.counterSale?.length > 0 &&
								(() => {
									const {changeGroup, others} = partitionChangeGroup<IRADAncillary>(ancillariesFormValues?.counterSale ?? []);
									return (
										<>
											{others.map(({ancillary, index}: IIndexedAncillary<IRADAncillary>) =>
												<AncillaryField key={`${ancillary.ancillaryCode}`} elem={ancillary} type={'counterSale'} index={index} ancillaryName={ancillaryName} disabled={true}/>
											)}
											{changeGroup.length > 0 &&
												<ChangeGroupField changeGroup={changeGroup} type={'counterSale'} ancillaryName={ancillaryName} />
											}
										</>
									);
								})()
						}

						{
							!ancillariesFormValues?.counterSale?.length &&
							<Grid item xs={12}>
								<EmptyState title={''} content={t('raDetail.ancillaries.message.noItems')}/>
							</Grid>
						}

						{
							!!ancillariesFormValues?.counterSale?.length &&
							<Grid item xs={12} sx={{pt: theme.spacing(2), display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"body"} component={"span"}>
									{t("raDetail.ancillaries.amount.added")}
								</Typography>
								<Typography variant={"body"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={raDetail?.quotationInfo.addedAncillariesAmount}
										currency={null}
									/>
								</Typography>
							</Grid>
						}
					</Grid>

					{/* Total Amount */}
					{
						(!!ancillariesFormValues?.booked?.length || !!ancillariesFormValues?.counterSale?.length) &&
						<Grid container columnSpacing={2} sx={{pt: theme.spacing(2)}}>
							<Grid item xs={12} sx={{display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"bodyLarge"} component={"span"}>
									{t("raDetail.ancillaries.amount.total")}
								</Typography>

								<Typography variant={"bodyLarge"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={raDetail?.quotationInfo.totAncillariesAmount}
										currency={null}
										fontWeight={"bodyLarge"}
									/>
								</Typography>
							</Grid>
						</Grid>
					}
				</AccordionDetails>
			</Accordion>

			<AncillaryHistorySection
				idRentalAgreement={idRentalAgreement}
				canView={!!canViewAncillaryHistory}
			/>
		</>
	);
}

export default AncillariesInfo;

/**
 * LPP-22293 / US-NTWRA-005 (RDT §3 SCFE-2) — contenitore read-only del controllo multi-scelta
 * Change Group nella vista di dettaglio. La selezione è derivata dalle unità dell'ancillary
 * (units/ancillaryUnits > 0) ed è presentata disabilitata: il dettaglio non altera payload né
 * prezzo (Requirement 2, RF-07). Riusa la logica di partizione e il componente di create-ra.
 */
const ChangeGroupField = ({changeGroup, type, ancillaryName}: {changeGroup: IIndexedAncillary<IRADAncillary>[], type: 'booked' | 'counterSale', ancillaryName: 'ancillaryNameIt' | 'ancillaryNameEn'}) => {
	const theme = useTheme();

	const options: IChangeGroupOption<IRADAncillary>[] = useMemo(
		() => changeGroup.map(({ancillary}) => ({
			code: ancillary.ancillaryCode,
			label: (ancillary[ancillaryName] as string) ?? ancillary.ancillaryCode,
			ancillary,
		})),
		[changeGroup, ancillaryName]
	);

	const selectedCodes = changeGroup
		.filter(({ancillary}) => Number(ancillary.ancillaryUnits ?? 0) > 0 || Number(ancillary.units ?? 0) > 0)
		.map(({ancillary}) => ancillary.ancillaryCode);

	return (
		<Grid container item xs={12} sx={{py: theme.spacing(1)}}>
			<Grid item xs={12}>
				<ChangeGroupMultiSelect<IRADAncillary>
					testId={type}
					options={options}
					selectedCodes={selectedCodes}
					onChange={() => { /* read-only in dettaglio */ }}
					disabled={true}
					renderPrice={(ancillary: IRADAncillary) => <AncillaryCostInfo elem={ancillary} />}
				/>
			</Grid>
			{/*
			  LPP-24810: il multi-select mostra il Change Group selezionato come chip (sola label): prezzo e
			  sconto banco (lordo barrato + netto + %) restano invisibili sulla riga. Si rende una riga per
			  ogni Change Group SELEZIONATO col dettaglio del costo, come nello "Storico modifiche accessori".
			*/}
			{
				changeGroup
					.filter(({ancillary}) => selectedCodes.includes(ancillary.ancillaryCode))
					.map(({ancillary}) => (
						<Grid container item xs={12} key={`cg-cost-${ancillary.ancillaryCode}`}
							  sx={{py: theme.spacing(0.5)}} data-testid={`change-group-cost-${ancillary.ancillaryCode}`}>
							<Grid item xs={8} sx={{display: 'flex', alignItems: 'center'}}>
								<Typography variant={'body'}>
									{(ancillary[ancillaryName] as string) ?? ancillary.ancillaryDefaultName ?? ancillary.ancillaryDefaultLabel ?? ancillary.ancillaryCode}
								</Typography>
							</Grid>
							<Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pr: theme.spacing(2)}}>
								<AncillaryCostInfo elem={ancillary}/>
							</Grid>
						</Grid>
					))
			}
		</Grid>
	);
};

/**
 * Ancillary cost info - cost currency /unit
 */
const AncillaryCostInfo = ({elem}: {elem: IRADAncillary}) => {
	const theme = useTheme();
	const {i18n} = useTranslation(['network']);

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(i18n.language);
	}, [i18n.language]);

	// LPP-24810: sulla riga accessorio mostra lo sconto banco come nello "Storico modifiche accessori":
	// lordo barrato + netto scontato + "% (-importo)". Prima si vedeva solo il netto (e per i Change Group
	// nemmeno quello sulla riga). Convenzione: `amountTaxIncluded` e' gia' il NETTO scontato; il lordo si
	// ricostruisce come netto + `discountAmount` (entrambi resi da PRC). Assente/zero -> solo il prezzo.
	const net = elem.amountTaxIncluded;
	const discountPercentage = elem?.discountPercentage;
	const discountAmount = elem?.discountAmount;
	const hasDeskDiscount = typeof discountPercentage === 'number' && discountPercentage > 0
		&& typeof discountAmount === 'number' && discountAmount > 0;
	const currency = elem?.ancillaryCurrency ?? undefined;
	const unitSuffix = elem.mileageNeeded ? '/km' : (elem.maxUnits > 1 || elem.ancillaryUnits > 1) ? '/u' : '';
	return (
		<Typography variant={'body'} component={'span'} sx={{mx: theme.spacing(1)}}>
			{
				hasDeskDiscount &&
				<Typography component={'span'} data-testid={'ancillary-gross'}
							sx={{textDecoration: 'line-through', color: theme.palette.text.disabled, mr: theme.spacing(0.5)}}>
					{formatCurrency(locale as LOCALES, net + (discountAmount as number), currency)}
				</Typography>
			}
			{formatCurrency(locale as LOCALES, net, currency)}{unitSuffix}
			{hasDeskDiscount ? ` ${formatDeskDiscount({discountPercentage, discountAmount}, locale as LOCALES, formatCurrency)}` : ''}
		</Typography>
	);
}

/**
 * Ancillary cost info - cost currency /unit
 */
export const AncillaryTotalCostInfo = ({amount, currency = null, fontWeight = 'body'}: {amount: number | null; currency: string | null; fontWeight?: 'body' | 'bodyLarge'}) => {
	const theme = useTheme();
	const {i18n} = useTranslation(['network']);

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(i18n.language);
	}, [i18n.language]);

	return (
		<>
			{
				<Typography variant={fontWeight} component={'span'} sx={{mx: theme.spacing(1)}}>
					<strong>{formatCurrency(locale as LOCALES, amount, currency ?? undefined)}</strong>
				</Typography>
			}
		</>
	);
}

const AncillaryField = ({elem, type, index, ancillaryName, disabled = false}: {elem: IRADAncillary, type: 'booked' | 'counterSale', index: number, ancillaryName: 'ancillaryNameIt' | 'ancillaryNameEn', disabled: boolean}) => {
	const theme = useTheme();
	const raDetailForm = useFormContext<IRaDetailView>();

	// Mostra stepper numerico se: maxUnits > 1, mileageNeeded, oppure ancillaryUnits > 1
	// (quest'ultimo caso copre gli accessori post-firma dove maxUnits non arriva dal catalogo PRC).
	const hasMultipleUnits = (elem.maxUnits > 1) || (elem.ancillaryUnits > 1);
	if (hasMultipleUnits || elem.mileageNeeded) {
		return (
			<Grid container item xs={12} key={`${type}_${elem.ancillaryCode}`} sx={{py: theme.spacing(1), mb: 'auto'}}>
				<Grid item xs={8} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start"}}>
					<Box sx={{width: '100px'}}>
						<FormNumberInput<IRaDetailView>
							name={`ancillariesInfo.${type}.${index}.${elem.mileageNeeded ? 'ancillaryMileage' : 'ancillaryUnits'}`}
							label={''}
							control={raDetailForm.control}
							minValue={0}
							maxValue={elem.mileageNeeded ? 999 : (elem.maxUnits > 1 ? elem.maxUnits : elem.ancillaryUnits)}
							disabled={disabled}
						/>
					</Box>
					<Tooltip title={(elem[ancillaryName] ?? elem.ancillaryDefaultName ?? elem.ancillaryDefaultLabel ?? elem.ancillaryCode) as string} disableInteractive sx={{pt: theme.spacing(2)}}>
						<Typography variant={'body'} sx={{ml: theme.spacing(2), py: theme.spacing(1)}}>
							{elem[ancillaryName] ?? elem.ancillaryDefaultName ?? elem.ancillaryDefaultLabel ?? elem.ancillaryCode}
						</Typography>
					</Tooltip>
				</Grid>
				<Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', py: theme.spacing(1), pr: theme.spacing(2)}}>
					<AncillaryCostInfo elem={elem}/>
				</Grid>

				{
					['DELIVERY', 'COLLECTION'].includes(elem.ancillarySubType as string) &&
					<Grid item xs={12} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pr: theme.spacing(2)}}>
						<FormTextField<IRaDetailView>
							name={`ancillariesInfo.${type}.${index}.ancillaryDeliveryCollectionAddress`}
							label={elem[ancillaryName]}
							control={raDetailForm.control}
							disabled={disabled}
						/>
					</Grid>
				}
			</Grid>
		)
	} else {
		return (
			<Grid container item xs={12} key={`${type}_${elem.ancillaryCode}`} sx={{py: theme.spacing(1)}}>
				<Grid item xs={8} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pt: theme.spacing(2)}}>
					<Tooltip title={elem.ancillaryDescr ?? ''} disableInteractive sx={{display: "flex", alignItems: "flex-end"}}>
						<span>
							<FormCheckbox<IRaDetailView>
								name={`ancillariesInfo.${type}.${index}.ancillaryUnits`}
								label={elem[ancillaryName] ?? elem.ancillaryDefaultName ?? elem.ancillaryDefaultLabel ?? elem.ancillaryCode ?? "Undefined"}
								disabled={disabled}
							/>
						</span>
					</Tooltip>
				</Grid>
				<Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', py: theme.spacing(1), pr: theme.spacing(2)}}>
					<AncillaryCostInfo elem={elem}/>
				</Grid>

				{
					['URBAN_DELIVERY', 'URBAN_COLLECTION'].includes(elem.ancillarySubType as string) &&
					<Grid item xs={12} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pr: theme.spacing(2)}}>
						<FormTextField<IRaDetailView>
							name={`ancillariesInfo.${type}.${index}.ancillaryDeliveryCollectionAddress`}
							label={elem[ancillaryName]}
							control={raDetailForm.control}
							disabled={disabled}
						/>
					</Grid>
				}
			</Grid>
		)
	}
}